import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Input,
  Button,
  Spin,
  Typography,
  Alert,
  List,
  Table,
  Tabs,
  Tag,
} from "antd";
import {
  SendOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
} from "@ant-design/icons";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import "./TeacherAgents.css";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const AnalyticsTeacherAgent = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [classData, setClassData] = useState([]);
  const [activeTab, setActiveTab] = useState("chat");
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch class data
  useEffect(() => {
    const fetchClassData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/teacher/classes", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data) {
          setClassData(response.data);
        }
      } catch (err) {
        console.error("Error fetching class data:", err);
      }
    };

    fetchClassData();
  }, []);

  // Initialize WebSocket connection
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication required");
      return;
    }

    const connectWebSocket = () => {
      const ws = new WebSocket(
        `ws://localhost:8000/ws/agent/analytics?token=${token}`
      );
      socketRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected - Analytics Agent");
        setConnected(true);
        setError(null);

        // Send initial message
        ws.send(
          JSON.stringify({
            message: "Hello, I need analytics assistance.",
            context: "teacher_analytics",
          })
        );
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setMessages((prev) => [
          ...prev,
          {
            text: data.message,
            sender: "agent",
            timestamp: new Date().toLocaleTimeString(),
            data: data.data || null, // Additional data for visualization
          },
        ]);
        setLoading(false);
      };

      ws.onerror = (e) => {
        console.error("WebSocket error:", e);
        setError("Connection error. Please try again.");
        setConnected(false);
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setConnected(false);

        // Attempt to reconnect after a delay
        setTimeout(() => {
          if (socketRef.current?.readyState === WebSocket.CLOSED) {
            connectWebSocket();
          }
        }, 3000);
      };
    };

    connectWebSocket();

    // Clean up on unmount
    return () => {
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []);

  // Handle sending messages
  const handleSend = () => {
    if (
      !input.trim() ||
      !socketRef.current ||
      socketRef.current.readyState !== WebSocket.OPEN
    )
      return;

    const message = {
      text: input,
      sender: "user",
      timestamp: new Date().toLocaleTimeString(),
    };

    setMessages((prev) => [...prev, message]);
    setLoading(true);
    setInput("");

    socketRef.current.send(
      JSON.stringify({
        message: input,
        context: "teacher_analytics",
      })
    );
  };

  // Handle pressing Enter to send message
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Mock data for the analytics dashboard - would be replaced with real data in production
  const studentPerformanceColumns = [
    { title: "Student", dataIndex: "student", key: "student" },
    { title: "Average Score", dataIndex: "avgScore", key: "avgScore" },
    {
      title: "Progress",
      dataIndex: "progress",
      key: "progress",
      render: (progress) => {
        let color = progress < 50 ? "red" : progress < 80 ? "orange" : "green";
        return <Tag color={color}>{progress}%</Tag>;
      },
    },
    {
      title: "Completion Rate",
      dataIndex: "completionRate",
      key: "completionRate",
      render: (rate) => {
        let color = rate < 50 ? "red" : rate < 80 ? "orange" : "green";
        return <Tag color={color}>{rate}%</Tag>;
      },
    },
  ];

  const mockStudentData = [
    {
      key: "1",
      student: "Student A",
      avgScore: 87,
      progress: 92,
      completionRate: 95,
    },
    {
      key: "2",
      student: "Student B",
      avgScore: 76,
      progress: 85,
      completionRate: 78,
    },
    {
      key: "3",
      student: "Student C",
      avgScore: 92,
      progress: 95,
      completionRate: 100,
    },
    {
      key: "4",
      student: "Student D",
      avgScore: 65,
      progress: 72,
      completionRate: 67,
    },
    {
      key: "5",
      student: "Student E",
      avgScore: 81,
      progress: 88,
      completionRate: 90,
    },
  ];

  return (
    <Layout>
      <div className="teacher-agent-container">
        <Card
          className="teacher-agent-card"
          title={
            <div className="agent-header">
              <Title level={3}>Analytics Assistant</Title>
              {connected ? (
                <Text type="success" className="connection-status">
                  Connected
                </Text>
              ) : (
                <Text type="danger" className="connection-status">
                  Disconnected
                </Text>
              )}
            </div>
          }
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            className="analytics-tabs"
          >
            <TabPane tab="Chat" key="chat">
              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  closable
                  className="error-alert"
                />
              )}

              <div className="message-container">
                {messages.length === 0 ? (
                  <div className="welcome-message">
                    <Title level={4}>Welcome to Analytics Assistant!</Title>
                    <Text>
                      I can help you understand student performance, engagement
                      metrics, and learning patterns. Ask me about:
                    </Text>
                    <ul>
                      <li>Student progress and performance trends</li>
                      <li>Class engagement metrics</li>
                      <li>Learning outcome analysis</li>
                      <li>Subject/topic difficulty analysis</li>
                    </ul>
                  </div>
                ) : (
                  <List
                    itemLayout="horizontal"
                    dataSource={messages}
                    renderItem={(message) => (
                      <div
                        className={`message ${
                          message.sender === "user"
                            ? "user-message"
                            : "agent-message"
                        }`}
                      >
                        <div className="message-content">
                          <div className="message-text">{message.text}</div>
                          {message.data && (
                            <div className="message-data">
                              {/* Placeholder for data visualization - would use a charting library in production */}
                              <Card size="small" className="data-card">
                                <Text type="secondary">
                                  Data visualization would appear here
                                </Text>
                              </Card>
                            </div>
                          )}
                          <div className="message-timestamp">
                            {message.timestamp}
                          </div>
                        </div>
                      </div>
                    )}
                  />
                )}
                {loading && (
                  <div className="agent-message">
                    <div className="message-content">
                      <div className="loading-indicator">
                        <Spin size="small" />{" "}
                        <Text italic>Processing analytics request...</Text>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="input-area">
                <TextArea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about student performance, engagement metrics, etc."
                  autoSize={{ minRows: 2, maxRows: 6 }}
                  disabled={!connected}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleSend}
                  disabled={!connected || !input.trim()}
                  className="send-button"
                >
                  Send
                </Button>
              </div>
            </TabPane>

            <TabPane tab="Dashboard" key="dashboard">
              <div className="analytics-dashboard">
                <div className="dashboard-section">
                  <Title level={4}>Class Performance Overview</Title>
                  <div className="analytics-cards">
                    <Card className="stat-card">
                      <Paragraph>Average Score</Paragraph>
                      <Title level={2}>78%</Title>
                      <BarChartOutlined className="stat-icon" />
                    </Card>
                    <Card className="stat-card">
                      <Paragraph>Completion Rate</Paragraph>
                      <Title level={2}>85%</Title>
                      <PieChartOutlined className="stat-icon" />
                    </Card>
                    <Card className="stat-card">
                      <Paragraph>Engagement</Paragraph>
                      <Title level={2}>72%</Title>
                      <LineChartOutlined className="stat-icon" />
                    </Card>
                  </div>
                </div>

                <div className="dashboard-section">
                  <Title level={4}>Student Performance</Title>
                  <Table
                    columns={studentPerformanceColumns}
                    dataSource={mockStudentData}
                    pagination={false}
                    className="analytics-table"
                  />
                </div>

                <div className="dashboard-section">
                  <Button type="primary" onClick={() => setActiveTab("chat")}>
                    Ask Analytics Assistant
                  </Button>
                </div>
              </div>
            </TabPane>
          </Tabs>
        </Card>
      </div>
    </Layout>
  );
};

export default AnalyticsTeacherAgent;
